// Anhänge für den Claude-Chat: Screenshots einfügen (Zwischenablage),
// Dateien hineinziehen oder auswählen (docs/claude.md).
//
// Bilder werden im Browser auf höchstens 1600 px verkleinert -- mehr sieht
// Claude ohnehin nicht, und ein Telefon-Screenshot hat sonst 4 MB. PNG
// bleibt PNG (Text in Screenshots bleibt scharf), solange es unter 3,5 MB
// bleibt; sonst JPEG. GIFs bleiben, wie sie sind (Bewegung). Alles andere
// geht unverändert hoch; Claude liest es über den Pfad auf forge.

export const DATEI_GRENZE = 20 * 1024 * 1024;
export const NACHRICHT_GRENZE = 50 * 1024 * 1024;
const BILD_KANTE = 1600;
const PNG_GRENZE = 3.5 * 1024 * 1024;

export interface NeuerAnhang {
  id: string;
  name: string;
  typ: string;
  /** Inhalt, base64 (so erwartet ihn der Dienst) */
  daten: string;
  groesse: number;
  /** Vorschau (Object-URL) für Bilder */
  vorschau?: string;
}

function zwei(n: number): string {
  return String(n).padStart(2, "0");
}

/** Ein eingefügter Screenshot hat keinen sinnvollen Namen („image.png"). */
function nameFuer(f: File): string {
  if (f.name && f.name !== "image.png" && f.name !== "blob") return f.name;
  const d = new Date();
  const endung = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
  return `Screenshot ${zwei(d.getHours())}-${zwei(d.getMinutes())}-${zwei(d.getSeconds())}.${endung}`;
}

function alsBase64(blob: Blob): Promise<string> {
  return new Promise((ok, fehler) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => fehler(r.error ?? new Error("Datei nicht lesbar"));
    r.readAsDataURL(blob);
  });
}

function canvasBlob(c: HTMLCanvasElement, typ: string, q?: number): Promise<Blob | null> {
  return new Promise((ok) => c.toBlob(ok, typ, q));
}

async function verkleinere(f: File): Promise<{ blob: Blob; typ: string }> {
  let bild: ImageBitmap;
  try {
    bild = await createImageBitmap(f);
  } catch {
    return { blob: f, typ: f.type };
  }
  const s = Math.min(1, BILD_KANTE / Math.max(bild.width, bild.height));
  if (s === 1 && f.size <= PNG_GRENZE) {
    bild.close();
    return { blob: f, typ: f.type };
  }
  const c = document.createElement("canvas");
  c.width = Math.round(bild.width * s);
  c.height = Math.round(bild.height * s);
  c.getContext("2d")?.drawImage(bild, 0, 0, c.width, c.height);
  bild.close();
  const png = await canvasBlob(c, "image/png");
  if (png && png.size <= PNG_GRENZE) return { blob: png, typ: "image/png" };
  const jpg = await canvasBlob(c, "image/jpeg", 0.88);
  return jpg ? { blob: jpg, typ: "image/jpeg" } : { blob: f, typ: f.type };
}

/** Eine Datei als Anhang. Wirft mit lesbarer Meldung, wenn sie zu groß ist. */
export async function alsAnhang(f: File): Promise<NeuerAnhang> {
  let blob: Blob = f;
  let typ = f.type || "application/octet-stream";
  let name = nameFuer(f);
  if (/^image\/(png|jpeg|webp)$/.test(typ)) {
    const v = await verkleinere(f);
    blob = v.blob;
    if (v.typ !== typ) name = name.replace(/\.[a-z0-9]+$/i, "") + (v.typ === "image/jpeg" ? ".jpg" : ".png");
    typ = v.typ;
  }
  if (blob.size > DATEI_GRENZE) {
    throw new Error(`„${f.name}" ist zu groß (höchstens 20 MB je Datei).`);
  }
  return {
    id: Math.random().toString(36).slice(2),
    name,
    typ,
    daten: await alsBase64(blob),
    groesse: blob.size,
    vorschau: typ.startsWith("image/") ? URL.createObjectURL(blob) : undefined,
  };
}

export function groesseText(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** Ein Anhang des Entwurfs geht gleich beim Einfügen hoch (claude/
 *  entwurf.go) -- mit Fortschritt, darum XMLHttpRequest statt fetch.
 *  kopf: was die Seite dazugibt (CSRF im Dashboard). Antwort: Status und
 *  Körper; die Fehler deutet der Aufrufer. */
export function ladeHoch(
  pfad: string,
  rumpf: unknown,
  kopf: Record<string, string>,
  fortschritt?: (anteil: number) => void,
): Promise<{ status: number; text: string }> {
  return new Promise((ok, fehler) => {
    const x = new XMLHttpRequest();
    x.open("POST", pfad);
    x.withCredentials = true;
    x.setRequestHeader("Content-Type", "application/json");
    x.setRequestHeader("Accept", "application/json");
    for (const [k, w] of Object.entries(kopf)) x.setRequestHeader(k, w);
    x.upload.onprogress = (e) => {
      if (e.lengthComputable) fortschritt?.(e.loaded / e.total);
    };
    x.onload = () => ok({ status: x.status, text: x.responseText });
    x.onerror = () => fehler(new Error("Hochladen gescheitert -- keine Verbindung."));
    x.send(JSON.stringify(rumpf));
  });
}
